import React, { useState, useEffect, useCallback } from 'react';
import './FacturesManagement.css';
import { Eye, FileDown, Pencil, Copy, Trash2, GraduationCap, Plus, BarChart3, CheckCircle, XCircle, AlertTriangle, Search, Coins, Calendar, Lightbulb, FileText, RefreshCw, CreditCard } from 'lucide-react';

const FacturesManagement = ({ user, token, onBack }) => {
  const [factures, setFactures] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [clients, setClients] = useState([]);
  const [devisAcceptes, setDevisAcceptes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFacture, setEditingFacture] = useState(null);
  const [viewingFacture, setViewingFacture] = useState(null);
  const [stats, setStats] = useState(null);
  const [filterStatut, setFilterStatut] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFromDevisModal, setShowFromDevisModal] = useState(false);
  const [selectedDevisForPaiement, setSelectedDevisForPaiement] = useState(null);
  const [devisPaiementForm, setDevisPaiementForm] = useState({
    date_paiement: new Date().toISOString().split('T')[0],
    montant_paye: '',
    mode_paiement: 'virement',
    reference_paiement: ''
  });
  const [clientSearch, setClientSearch] = useState('');
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);

  // États pour les paiements
  const [paiements, setPaiements] = useState([]);
  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [editingPaiement, setEditingPaiement] = useState(null);
  const [echeancesDisponibles, setEcheancesDisponibles] = useState([]);
  const [paiementForm, setPaiementForm] = useState({
    montant: '',
    date_paiement: new Date().toISOString().split('T')[0],
    mode_paiement: 'virement',
    reference: '',
    notes: '',
    echeance_id: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    statut: 'payee',
    notes: '',
    conditions: 'Paiement à réception. TVA 20%.',
    lignes: [],
    date_paiement: new Date().toISOString().split('T')[0],
    montant_paye: '',
    mode_paiement: 'virement',
    reference_paiement: ''
  });

  const fetchFactures = useCallback(async () => {
    try {
      const response = await fetch(`/api/factures`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setFactures(data);
    } catch (error) {
      console.error('Erreur chargement factures:', error);
    }
  }, [token]);

  const fetchPrestations = useCallback(async () => {
    try {
      const response = await fetch(`/api/prestations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPrestations(data);
    } catch (error) {
      console.error('Erreur chargement prestations:', error);
    }
  }, [token]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await fetch(`/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setClients(data);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    }
  }, [token]);

  const fetchDevisDisponibles = useCallback(async () => {
    try {
      const response = await fetch(`/api/devis`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      // Filtrer les devis disponibles (brouillon, envoyé, accepté) - exclure refusé et expiré
      setDevisAcceptes(data.filter(d => d.statut !== 'refuse' && d.statut !== 'expire'));
    } catch (error) {
      console.error('Erreur chargement devis:', error);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`/api/factures-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  }, [token]);

  // Fonctions pour les paiements
  const fetchPaiements = async (factureId) => {
    try {
      const response = await fetch(`/api/factures/${factureId}/paiements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPaiements(data.paiements || []);
      return data;
    } catch (error) {
      console.error('Erreur chargement paiements:', error);
      return { paiements: [], total_paye: 0 };
    }
  };

  const fetchEcheancesDisponibles = async (factureId) => {
    try {
      const response = await fetch(`/api/factures/${factureId}/echeances-disponibles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setEcheancesDisponibles(data);
    } catch (error) {
      console.error('Erreur chargement écheances:', error);
      setEcheancesDisponibles([]);
    }
  };

  const openPaiementModal = (factureId, echeance = null) => {
    setPaiementForm({
      montant: echeance ? echeance.montant.toString() : '',
      date_paiement: new Date().toISOString().split('T')[0],
      mode_paiement: 'virement',
      reference: '',
      notes: '',
      echeance_id: echeance ? echeance.id.toString() : ''
    });
    setEditingPaiement(null);
    setShowPaiementModal(true);
  };

  const openEditPaiement = (paiement) => {
    setPaiementForm({
      montant: paiement.montant.toString(),
      date_paiement: paiement.date_paiement.split('T')[0],
      mode_paiement: paiement.mode_paiement,
      reference: paiement.reference || '',
      notes: paiement.notes || '',
      echeance_id: paiement.echeance_id ? paiement.echeance_id.toString() : ''
    });
    setEditingPaiement(paiement);
    setShowPaiementModal(true);
  };

  const handlePaiementSubmit = async (e) => {
    e.preventDefault();
    if (!viewingFacture) return;

    try {
      const url = editingPaiement
        ? `/api/factures/${viewingFacture.id}/paiements/${editingPaiement.id}`
        : `/api/factures/${viewingFacture.id}/paiements`;

      const response = await fetch(url, {
        method: editingPaiement ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...paiementForm,
          montant: parseFloat(paiementForm.montant),
          echeance_id: paiementForm.echeance_id ? parseInt(paiementForm.echeance_id) : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur lors de l\'enregistrement');
      }

      const result = await response.json();
      setShowPaiementModal(false);

      // Recharger les paiements et la facture
      await fetchPaiements(viewingFacture.id);
      await fetchFactures();
      await fetchStats();

      // Mettre à jour la facture en cours de visualisation
      if (result.facture_payee) {
        setViewingFacture(prev => ({ ...prev, statut: 'payee', montant_paye: result.total_paye }));
      } else {
        setViewingFacture(prev => ({ ...prev, montant_paye: result.total_paye }));
      }
    } catch (error) {
      console.error('Erreur paiement:', error);
      alert(error.message);
    }
  };

  const deletePaiement = async (paiementId) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    if (!viewingFacture) return;

    try {
      const response = await fetch(`/api/factures/${viewingFacture.id}/paiements/${paiementId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur lors de la suppression');
      }

      const result = await response.json();

      // Recharger les données
      await fetchPaiements(viewingFacture.id);
      await fetchFactures();
      await fetchStats();

      // Mettre à jour la facture
      setViewingFacture(prev => ({ ...prev, montant_paye: result.total_paye, statut: result.total_paye >= parseFloat(prev.montant_ttc) ? 'payee' : 'emise' }));
    } catch (error) {
      console.error('Erreur suppression paiement:', error);
      alert(error.message);
    }
  };

  const getModeLabel = (mode) => {
    const labels = {
      virement: 'Virement',
      cheque: 'Chèque',
      carte: 'Carte bancaire',
      especes: 'Espèces',
      prelevement: 'Prélèvement',
      autre: 'Autre'
    };
    return labels[mode] || mode;
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchFactures(), fetchPrestations(), fetchClients(), fetchDevisDisponibles(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchFactures, fetchPrestations, fetchClients, fetchDevisDisponibles, fetchStats]);

  const openNewFacture = () => {
    setFormData({
      client_id: '',
      statut: 'payee',
      notes: '',
      conditions: 'Paiement à réception. TVA 20%.',
      lignes: [{
        description: '',
        prix_unitaire_ht: '',
        quantite: 1,
        prestation_id: null,
        is_formation: false,
        formation_intitule: '',
        formation_convention_numero: '',
        formation_convention_date: '',
        formation_beneficiaires: '',
        formation_modalite: '',
        formation_duree_heures: '',
        formation_date_debut: '',
        formation_date_fin: ''
      }],
      date_paiement: new Date().toISOString().split('T')[0],
      montant_paye: '',
      mode_paiement: 'virement',
      reference_paiement: ''
    });
    setClientSearch('');
    setShowClientSuggestions(false);
    setEditingFacture(null);
    setShowModal(true);
  };

  const openEditFacture = async (factureItem) => {
    try {
      const response = await fetch(`/api/factures/${factureItem.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      setFormData({
        client_id: data.client_id,
        statut: data.statut || 'brouillon',
        notes: data.notes || '',
        conditions: data.conditions || '',
        lignes: data.lignes.map(l => ({
          description: l.description,
          prix_unitaire_ht: l.prix_unitaire_ht,
          quantite: l.quantite,
          prestation_id: l.prestation_id,
          is_formation: l.formation_intitule ? true : false,
          formation_intitule: l.formation_intitule || '',
          formation_convention_numero: l.formation_convention_numero || '',
          formation_convention_date: l.formation_convention_date ? l.formation_convention_date.split('T')[0] : '',
          formation_beneficiaires: l.formation_beneficiaires || '',
          formation_modalite: l.formation_modalite || '',
          formation_duree_heures: l.formation_duree_heures || '',
          formation_date_debut: l.formation_date_debut ? l.formation_date_debut.split('T')[0] : '',
          formation_date_fin: l.formation_date_fin ? l.formation_date_fin.split('T')[0] : ''
        }))
      });
      // Initialiser la recherche avec le nom du client
      setClientSearch(data.client_nom + ' - ' + data.client_entreprise);
      setShowClientSuggestions(false);
      setEditingFacture(data);
      setShowModal(true);
    } catch (error) {
      console.error('Erreur chargement facture:', error);
    }
  };

  const viewFactureDetails = async (factureItem) => {
    try {
      const response = await fetch(`/api/factures/${factureItem.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setViewingFacture(data);
      // Charger les paiements et échéances disponibles
      await fetchPaiements(factureItem.id);
      await fetchEcheancesDisponibles(factureItem.id);
    } catch (error) {
      console.error('Erreur chargement facture:', error);
    }
  };

  const addLigne = () => {
    setFormData({
      ...formData,
      lignes: [...formData.lignes, {
        description: '',
        prix_unitaire_ht: '',
        quantite: 1,
        prestation_id: null,
        is_formation: false,
        formation_intitule: '',
        formation_convention_numero: '',
        formation_convention_date: '',
        formation_beneficiaires: '',
        formation_modalite: '',
        formation_duree_heures: '',
        formation_date_debut: '',
        formation_date_fin: ''
      }]
    });
  };

  const removeLigne = (index) => {
    const newLignes = formData.lignes.filter((_, i) => i !== index);
    setFormData({ ...formData, lignes: newLignes });
  };

  const updateLigne = (index, field, value) => {
    const newLignes = [...formData.lignes];
    newLignes[index][field] = value;
    setFormData({ ...formData, lignes: newLignes });
  };

  const selectPrestation = (index, prestationId) => {
    const prestation = prestations.find(p => p.id === parseInt(prestationId));
    if (prestation) {
      const newLignes = [...formData.lignes];
      newLignes[index] = {
        ...newLignes[index],
        prestation_id: prestation.id,
        description: prestation.nom,
        prix_unitaire_ht: prestation.prix_ht,
        is_formation: prestation.is_formation || false,
        formation_intitule: prestation.is_formation ? prestation.nom : '',
        formation_convention_numero: '',
        formation_convention_date: '',
        formation_beneficiaires: '',
        formation_modalite: '',
        formation_duree_heures: '',
        formation_date_debut: '',
        formation_date_fin: ''
      };
      setFormData({ ...formData, lignes: newLignes });
    }
  };

  const calculateTotals = () => {
    let totalHT = 0;

    formData.lignes.forEach(ligne => {
      const quantite = parseFloat(ligne.quantite) || 1;
      totalHT += (parseFloat(ligne.prix_unitaire_ht) || 0) * quantite;
    });

    const tva = totalHT * 0.20;
    const totalTTC = totalHT + tva;

    return { totalHT, tva, totalTTC };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.client_id) {
      alert('Veuillez sélectionner un client');
      return;
    }

    if (formData.lignes.length === 0 || !formData.lignes.some(l => l.description && l.prix_unitaire_ht)) {
      alert('Veuillez ajouter au moins une ligne');
      return;
    }

    const { totalTTC: ttcCalc } = calculateTotals();
    const montantPayeNum = parseFloat(formData.montant_paye);

    if (!editingFacture) {
      if (!formData.date_paiement) {
        alert('La date de paiement est obligatoire');
        return;
      }
      if (!montantPayeNum || montantPayeNum < ttcCalc - 0.01) {
        alert(`Le montant payé doit être ≥ au total TTC (${ttcCalc.toFixed(2)} €)`);
        return;
      }
      if (!formData.mode_paiement) {
        alert('Le mode de paiement est obligatoire');
        return;
      }
    }

    try {
      const url = editingFacture
        ? `/api/factures/${editingFacture.id}`
        : `/api/factures`;

      const method = editingFacture ? 'PUT' : 'POST';

      const body = {
        client_id: formData.client_id,
        statut: formData.statut,
        notes: formData.notes,
        conditions: formData.conditions,
        lignes: formData.lignes.filter(l => l.description && l.prix_unitaire_ht)
      };

      if (!editingFacture) {
        body.date_paiement = formData.date_paiement;
        body.montant_paye = montantPayeNum;
        body.mode_paiement = formData.mode_paiement;
        body.reference_paiement = formData.reference_paiement;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        setShowModal(false);
        fetchFactures();
        fetchStats();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde facture:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const updateStatut = async (factureId, newStatut) => {
    try {
      const response = await fetch(`/api/factures/${factureId}/statut`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ statut: newStatut })
      });

      if (response.ok) {
        fetchFactures();
        fetchStats();
      }
    } catch (error) {
      console.error('Erreur changement statut:', error);
    }
  };

  const deleteFacture = async (factureId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) return;

    try {
      const response = await fetch(`/api/factures/${factureId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchFactures();
        fetchStats();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression facture:', error);
    }
  };

  const downloadPDF = async (factureId, numeroFacture) => {
    try {
      const response = await fetch(`/api/factures/${factureId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${numeroFacture}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error);
    }
  };

  const duplicateFacture = async (factureId) => {
    try {
      const response = await fetch(`/api/factures/${factureId}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchFactures();
        fetchStats();
        alert('Facture dupliquée avec succès');
      }
    } catch (error) {
      console.error('Erreur duplication:', error);
    }
  };

  const openPaiementForDevis = (devis) => {
    setSelectedDevisForPaiement(devis);
    setDevisPaiementForm({
      date_paiement: new Date().toISOString().split('T')[0],
      montant_paye: parseFloat(devis.total_ttc).toFixed(2),
      mode_paiement: 'virement',
      reference_paiement: ''
    });
  };

  const createFromDevis = async () => {
    if (!selectedDevisForPaiement) return;

    const ttc = parseFloat(selectedDevisForPaiement.total_ttc);
    const paye = parseFloat(devisPaiementForm.montant_paye);
    if (!devisPaiementForm.date_paiement) {
      alert('La date de paiement est obligatoire');
      return;
    }
    if (!paye || paye < ttc - 0.01) {
      alert(`Le montant payé doit être ≥ au total TTC (${ttc.toFixed(2)} €)`);
      return;
    }

    try {
      const response = await fetch(`/api/factures/from-devis/${selectedDevisForPaiement.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date_paiement: devisPaiementForm.date_paiement,
          montant_paye: paye,
          mode_paiement: devisPaiementForm.mode_paiement,
          reference_paiement: devisPaiementForm.reference_paiement
        })
      });

      if (response.ok) {
        const data = await response.json();
        setShowFromDevisModal(false);
        setSelectedDevisForPaiement(null);
        fetchFactures();
        fetchStats();
        fetchDevisDisponibles();
        alert(`Facture ${data.numero_facture} créée depuis le devis ${data.devis_numero}`);
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Erreur création depuis devis:', error);
    }
  };

  const getStatutBadge = (statut) => {
    const badges = {
      brouillon: { class: 'badge-draft', label: 'Brouillon' },
      emise: { class: 'badge-sent', label: 'Émise' },
      payee: { class: 'badge-paid', label: 'Payée' },
      annulee: { class: 'badge-cancelled', label: 'Annulée' }
    };
    const badge = badges[statut] || badges.brouillon;
    return <span className={`facture-badge ${badge.class}`}>{badge.label}</span>;
  };

  const filteredFactures = factures.filter(f => {
    const matchStatut = filterStatut === 'all' || f.statut === filterStatut;
    const matchSearch = searchTerm === '' ||
      f.numero_facture?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.client_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.client_entreprise?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchStatut && matchSearch;
  });

  const { totalHT, tva, totalTTC } = calculateTotals();

  if (loading) {
    return (
      <div className="factures-management">
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="factures-management">
      <div className="factures-header">
        <div className="header-left">
          <button className="btn-back" onClick={onBack}>Retour</button>
          <h1>Gestion des Factures</h1>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => setShowFromDevisModal(true)}>
            Créer depuis un devis
          </button>
          <button className="btn-primary" onClick={openNewFacture}>
            + Nouvelle Facture
          </button>
        </div>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="factures-stats">
          <div className="stat-card stat-draft">
            <div className="stat-value">{stats.brouillons || 0}</div>
            <div className="stat-label">Brouillons</div>
          </div>
          <div className="stat-card stat-sent">
            <div className="stat-value">{stats.emises || 0}</div>
            <div className="stat-label">Émises</div>
          </div>
          <div className="stat-card stat-paid">
            <div className="stat-value">{stats.payees || 0}</div>
            <div className="stat-label">Payées</div>
          </div>
          <div className="stat-card stat-amount">
            <div className="stat-value">{parseFloat(stats.montant_paye || 0).toLocaleString('fr-FR')} €</div>
            <div className="stat-label">Encaissé</div>
          </div>
          <div className="stat-card stat-pending">
            <div className="stat-value">{parseFloat(stats.montant_en_attente || 0).toLocaleString('fr-FR')} €</div>
            <div className="stat-label">En attente</div>
          </div>
          <div className="stat-card stat-month">
            <div className="stat-value">{parseFloat(stats.ca_mois || 0).toLocaleString('fr-FR')} €</div>
            <div className="stat-label">CA du mois</div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="factures-filters">
        <input
          type="text"
          className="search-input"
          placeholder="Rechercher (numéro, client...)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="filter-select"
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
        >
          <option value="all">Tous les statuts</option>
          <option value="brouillon">Brouillons</option>
          <option value="emise">Émises</option>
          <option value="payee">Payées</option>
          <option value="annulee">Annulées</option>
        </select>
      </div>

      {/* Liste des factures */}
      <div className="factures-list">
        {filteredFactures.length === 0 ? (
          <div className="empty-state">
            <p>Aucune facture trouvée</p>
            <button className="btn-primary" onClick={openNewFacture}>
              Créer votre première facture
            </button>
          </div>
        ) : (
          <table className="factures-table">
            <thead>
              <tr>
                <th>N° Facture</th>
                <th>Client</th>
                <th>Date</th>
                <th>Montant TTC</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFactures.map(facture => (
                <tr key={facture.id}>
                  <td>
                    <span className="facture-numero">{facture.numero_facture}</span>
                    {facture.numero_devis && (
                      <div className="from-devis">Depuis {facture.numero_devis}</div>
                    )}
                  </td>
                  <td>
                    <div className="client-info">
                      <span className="client-name">{facture.client_nom}</span>
                      <span className="client-company">{facture.client_entreprise}</span>
                    </div>
                  </td>
                  <td>{new Date(facture.date_emission).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <span className="montant">{parseFloat(facture.montant_ttc).toLocaleString('fr-FR')} €</span>
                  </td>
                  <td>{getStatutBadge(facture.statut)}</td>
                  <td>
                    <div className="actions-cell">
                      <button
                        className="btn-icon"
                        onClick={() => viewFactureDetails(facture)}
                        title="Voir détails"
                      >
                        <Eye size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => downloadPDF(facture.id, facture.numero_facture)}
                        title="Télécharger PDF"
                      >
                        <FileDown size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                      </button>
                      {facture.statut !== 'annulee' && (
                        <button
                          className="btn-icon"
                          onClick={() => openEditFacture(facture)}
                          title="Modifier"
                        >
                          <Pencil size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                        </button>
                      )}
                      {facture.statut === 'brouillon' && (
                        <button
                          className="btn-small btn-send"
                          onClick={() => updateStatut(facture.id, 'emise')}
                        >
                          Émettre
                        </button>
                      )}
                      {facture.statut === 'emise' && (
                        <button
                          className="btn-small btn-paid"
                          onClick={() => updateStatut(facture.id, 'payee')}
                        >
                          Marquer payée
                        </button>
                      )}
                      <button
                        className="btn-icon"
                        onClick={() => duplicateFacture(facture.id)}
                        title="Dupliquer"
                      >
                        <Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                      </button>
                      {facture.statut === 'brouillon' && (
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => deleteFacture(facture.id)}
                          title="Supprimer"
                        >
                          <Trash2 size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Création/Édition */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h2>{editingFacture ? 'Modifier la facture' : 'Nouvelle facture'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Sélection client et statut */}
                <div className="form-row">
                  <div className="form-group flex-2">
                    <label>Client *</label>
                    <div className="client-autocomplete">
                      <input
                        type="text"
                        className="client-search-input"
                        placeholder="Tapez pour rechercher un client..."
                        value={clientSearch}
                        onChange={(e) => {
                          setClientSearch(e.target.value);
                          setShowClientSuggestions(true);
                          if (e.target.value === '') {
                            setFormData({ ...formData, client_id: '' });
                          }
                        }}
                        onFocus={() => setShowClientSuggestions(true)}
                      />
                      {showClientSuggestions && clientSearch.length > 0 && (() => {
                        const q = clientSearch.toLowerCase();
                        const matches = (Array.isArray(clients) ? clients : []).filter(client =>
                          String(client.nom || '').toLowerCase().includes(q) ||
                          String(client.entreprise || '').toLowerCase().includes(q) ||
                          String(client.email || '').toLowerCase().includes(q)
                        );
                        return (
                          <div className="client-suggestions">
                            {matches.slice(0, 8).map(client => (
                              <div
                                key={client.id}
                                className={`client-suggestion-item ${formData.client_id === client.id ? 'selected' : ''}`}
                                onClick={() => {
                                  setFormData({ ...formData, client_id: client.id });
                                  setClientSearch(`${client.nom || ''}${client.entreprise ? ' - ' + client.entreprise : ''}`);
                                  setShowClientSuggestions(false);
                                }}
                              >
                                <span className="suggestion-name">{client.nom || '(sans nom)'}</span>
                                <span className="suggestion-company">{client.entreprise || ''}</span>
                              </div>
                            ))}
                            {matches.length === 0 && (
                              <div className="no-suggestions">Aucun client trouvé</div>
                            )}
                          </div>
                        );
                      })()}
                      {formData.client_id && (
                        <input type="hidden" name="client_id" value={formData.client_id} required />
                      )}
                    </div>
                  </div>
                  {editingFacture && (
                    <div className="form-group flex-1">
                      <label>Statut de la facture</label>
                      <select
                        value={formData.statut}
                        onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                        className={`statut-select statut-${formData.statut}`}
                      >
                        <option value="brouillon">Brouillon</option>
                        <option value="emise">Émise</option>
                        <option value="payee">Payée</option>
                        <option value="annulee">Annulée</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Lignes de facture */}
                <div className="facture-lignes-section">
                  <div className="section-header">
                    <h3>Lignes de la facture</h3>
                    <button type="button" className="btn-add-ligne" onClick={addLigne}>
                      + Ajouter une ligne
                    </button>
                  </div>

                  {formData.lignes.map((ligne, index) => (
                    <div key={index} className="ligne-row">
                      <div className="ligne-prestation">
                        <select
                          value={ligne.prestation_id || ''}
                          onChange={(e) => selectPrestation(index, e.target.value)}
                        >
                          <option value="">Prestation...</option>
                          {prestations.map(p => (
                            <option key={p.id} value={p.id}>{p.is_formation ? '🎓 ' : ''}{p.nom}</option>
                          ))}
                        </select>
                      </div>
                      <div className="ligne-description">
                        <input
                          type="text"
                          placeholder="Description"
                          value={ligne.description}
                          onChange={(e) => updateLigne(index, 'description', e.target.value)}
                        />
                      </div>
                      <div className="ligne-quantite">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={ligne.quantite}
                          onChange={(e) => updateLigne(index, 'quantite', e.target.value)}
                        />
                      </div>
                      <div className="ligne-prix">
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Prix HT"
                          value={ligne.prix_unitaire_ht}
                          onChange={(e) => updateLigne(index, 'prix_unitaire_ht', e.target.value)}
                        />
                        <span className="currency">€</span>
                      </div>
                      <div className="ligne-total">
                        {((parseFloat(ligne.prix_unitaire_ht) || 0) * (parseFloat(ligne.quantite) || 1)).toFixed(2)} €
                      </div>
                      <button
                        type="button"
                        className="btn-remove-ligne"
                        onClick={() => removeLigne(index)}
                        disabled={formData.lignes.length === 1}
                      >
                        ×
                      </button>

                      {/* Champs Formation Professionnelle */}
                      {ligne.is_formation && (
                        <div className="formation-fields" style={{
                          width: '100%',
                          backgroundColor: '#f8f4ff',
                          border: '2px solid #6f42c1',
                          borderRadius: '10px',
                          padding: '15px',
                          marginTop: '10px',
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '15px'
                        }}>
                          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                            <span style={{ fontSize: '18px' }}><GraduationCap size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /></span>
                            <strong style={{ color: '#6f42c1' }}>Formation Professionnelle - Mentions obligatoires</strong>
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Intitulé de la formation *</label>
                            <input
                              type="text"
                              placeholder="Ex: CYBERSECURITE - SENSIBILISATION DES UTILISATEURS"
                              value={ligne.formation_intitule}
                              onChange={(e) => updateLigne(index, 'formation_intitule', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>N° Convention/Contrat</label>
                            <input
                              type="text"
                              placeholder="Ex: CONV-2026-001"
                              value={ligne.formation_convention_numero}
                              onChange={(e) => updateLigne(index, 'formation_convention_numero', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Date convention</label>
                            <input
                              type="date"
                              value={ligne.formation_convention_date}
                              onChange={(e) => updateLigne(index, 'formation_convention_date', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Bénéficiaire(s) *</label>
                            <input
                              type="text"
                              placeholder="Nom du stagiaire ou 'Groupe de X stagiaires'"
                              value={ligne.formation_beneficiaires}
                              onChange={(e) => updateLigne(index, 'formation_beneficiaires', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Modalité *</label>
                            <select
                              value={ligne.formation_modalite}
                              onChange={(e) => updateLigne(index, 'formation_modalite', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            >
                              <option value="">-- Sélectionner --</option>
                              <option value="presentiel">Présentiel</option>
                              <option value="distanciel">Distanciel</option>
                              <option value="mixte">Mixte</option>
                            </select>
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Durée (heures) *</label>
                            <input
                              type="number"
                              min="1"
                              placeholder="Ex: 14"
                              value={ligne.formation_duree_heures}
                              onChange={(e) => updateLigne(index, 'formation_duree_heures', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Date début</label>
                            <input
                              type="date"
                              value={ligne.formation_date_debut}
                              onChange={(e) => updateLigne(index, 'formation_date_debut', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Date fin</label>
                            <input
                              type="date"
                              value={ligne.formation_date_fin}
                              onChange={(e) => updateLigne(index, 'formation_date_fin', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div style={{ gridColumn: '1 / -1', fontSize: '11px', color: '#6f42c1', fontStyle: 'italic', marginTop: '5px' }}>
                            <Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Mention légale ajoutée : "Formation professionnelle au sens de l'article L.6313-1 du Code du travail"
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Totaux */}
                <div className="facture-totaux">
                  <h4>Récapitulatif</h4>
                  <div className="total-row">
                    <span>Total HT</span>
                    <span>{totalHT.toFixed(2)} €</span>
                  </div>
                  <div className="total-row">
                    <span>TVA (20%)</span>
                    <span>{tva.toFixed(2)} €</span>
                  </div>
                  <div className="total-row total-ttc">
                    <span>Total TTC</span>
                    <span>{totalTTC.toFixed(2)} €</span>
                  </div>
                </div>

                {/* Notes et conditions */}
                <div className="form-row">
                  <div className="form-group flex-1">
                    <label>Notes</label>
                    <textarea
                      rows="2"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Notes internes ou pour le client..."
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group flex-1">
                    <label>Conditions de paiement</label>
                    <textarea
                      rows="2"
                      value={formData.conditions}
                      onChange={(e) => setFormData({ ...formData, conditions: e.target.value })}
                    />
                  </div>
                </div>

                {!editingFacture && (
                  <div className="paiement-section" style={{
                    marginTop: '20px',
                    padding: '16px',
                    background: '#f0f9ff',
                    border: '2px solid #0ea5e9',
                    borderRadius: '8px'
                  }}>
                    <h3 style={{ marginTop: 0, color: '#0c4a6e' }}>
                      Paiement (obligatoire — la facture est émise à la date du paiement)
                    </h3>
                    <div className="form-row" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <div className="form-group" style={{ flex: '1 1 180px' }}>
                        <label>Date du paiement *</label>
                        <input
                          type="date"
                          value={formData.date_paiement}
                          onChange={(e) => setFormData({ ...formData, date_paiement: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group" style={{ flex: '1 1 180px' }}>
                        <label>Montant payé (TTC) *</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.montant_paye}
                          onChange={(e) => setFormData({ ...formData, montant_paye: e.target.value })}
                          placeholder={totalTTC.toFixed(2)}
                          required
                        />
                      </div>
                      <div className="form-group" style={{ flex: '1 1 180px' }}>
                        <label>Mode de paiement *</label>
                        <select
                          value={formData.mode_paiement}
                          onChange={(e) => setFormData({ ...formData, mode_paiement: e.target.value })}
                          required
                        >
                          <option value="virement">Virement</option>
                          <option value="cb">Carte bancaire</option>
                          <option value="especes">Espèces</option>
                          <option value="cheque">Chèque</option>
                          <option value="prelevement">Prélèvement</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 180px' }}>
                        <label>Référence (facultatif)</label>
                        <input
                          type="text"
                          value={formData.reference_paiement}
                          onChange={(e) => setFormData({ ...formData, reference_paiement: e.target.value })}
                          placeholder="N° transaction, chèque..."
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingFacture ? 'Enregistrer' : 'Créer la facture'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Détails */}
      {viewingFacture && (
        <div className="modal-overlay">
          <div className="modal-content modal-large facture-preview">
            <div className="modal-header">
              <h2>Facture {viewingFacture.numero_facture}</h2>
              <button className="modal-close" onClick={() => setViewingFacture(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="preview-header">
                <div className="preview-info">
                  <p><strong>Client:</strong> {viewingFacture.client_nom}</p>
                  <p><strong>Entreprise:</strong> {viewingFacture.client_entreprise}</p>
                  <p><strong>Adresse:</strong> {viewingFacture.client_adresse}</p>
                  {viewingFacture.client_siret && (
                    <p><strong>SIRET:</strong> {viewingFacture.client_siret}</p>
                  )}
                </div>
                <div className="preview-dates">
                  <p><strong>Date d'émission:</strong> {new Date(viewingFacture.date_emission).toLocaleDateString('fr-FR')}</p>
                  <p><strong>Statut:</strong> {getStatutBadge(viewingFacture.statut)}</p>
                  {viewingFacture.numero_devis && (
                    <p><strong>Depuis devis:</strong> {viewingFacture.numero_devis}</p>
                  )}
                </div>
              </div>

              <table className="preview-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qté</th>
                    <th>Prix U. HT</th>
                    <th>Total HT</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingFacture.lignes?.map((ligne, i) => (
                    <tr key={i}>
                      <td>{ligne.description}</td>
                      <td>{ligne.quantite}</td>
                      <td>{parseFloat(ligne.prix_unitaire_ht).toFixed(2)} €</td>
                      <td>{parseFloat(ligne.total_ht).toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="preview-totaux">
                <div className="total-line">
                  <span>Total HT</span>
                  <span>{parseFloat(viewingFacture.montant_ht).toFixed(2)} €</span>
                </div>
                <div className="total-line">
                  <span>TVA ({viewingFacture.tva_taux}%)</span>
                  <span>{(parseFloat(viewingFacture.montant_ht) * parseFloat(viewingFacture.tva_taux) / 100).toFixed(2)} €</span>
                </div>
                <div className="total-line total-ttc">
                  <span>Total TTC</span>
                  <span>{parseFloat(viewingFacture.montant_ttc).toFixed(2)} €</span>
                </div>
              </div>

              {viewingFacture.notes && (
                <div className="preview-notes">
                  <strong>Notes:</strong>
                  <p>{viewingFacture.notes}</p>
                </div>
              )}

              {viewingFacture.conditions && (
                <div className="preview-conditions">
                  <strong>Conditions:</strong>
                  <p>{viewingFacture.conditions}</p>
                </div>
              )}

              {/* Section Paiements */}
              <div className="paiements-section">
                <div className="paiements-header">
                  <h3>Paiements</h3>
                  <button className="btn-add-paiement" onClick={() => openPaiementModal(viewingFacture.id)}>
                    + Ajouter un paiement
                  </button>
                </div>

                {/* Résumé des paiements */}
                <div className="paiements-resume">
                  <div className="resume-item">
                    <span>Total TTC:</span>
                    <span className="montant">{parseFloat(viewingFacture.montant_ttc).toFixed(2)} €</span>
                  </div>
                  <div className="resume-item paye">
                    <span>Total payé:</span>
                    <span className="montant">{paiements.reduce((sum, p) => sum + parseFloat(p.montant), 0).toFixed(2)} €</span>
                  </div>
                  <div className={`resume-item reste ${paiements.reduce((sum, p) => sum + parseFloat(p.montant), 0) >= parseFloat(viewingFacture.montant_ttc) ? 'solde' : ''}`}>
                    <span>Reste à payer:</span>
                    <span className="montant">
                      {Math.max(0, parseFloat(viewingFacture.montant_ttc) - paiements.reduce((sum, p) => sum + parseFloat(p.montant), 0)).toFixed(2)} €
                    </span>
                  </div>
                </div>

                {/* Échéances disponibles du client */}
                {echeancesDisponibles.length > 0 && (
                  <div className="echeances-disponibles">
                    <h4>Échéances client en attente</h4>
                    <div className="echeances-list">
                      {echeancesDisponibles.map(ech => (
                        <div key={ech.id} className={`echeance-item ${ech.statut}`}>
                          <div className="echeance-info">
                            <span className="echeance-numero">Échéance {ech.echeance_numero}</span>
                            <span className="echeance-date">
                              {new Date(ech.date_prevue).toLocaleDateString('fr-FR')}
                            </span>
                            <span className="echeance-montant">{parseFloat(ech.montant).toFixed(2)} €</span>
                            <span className={`echeance-statut ${ech.statut}`}>
                              {ech.statut === 'en_attente' ? 'En attente' : 'En retard'}
                            </span>
                          </div>
                          <button
                            className="btn-importer-echeance"
                            onClick={() => openPaiementModal(viewingFacture.id, ech)}
                          >
                            Enregistrer paiement
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Liste des paiements */}
                {paiements.length > 0 ? (
                  <table className="paiements-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Montant</th>
                        <th>Mode</th>
                        <th>Référence</th>
                        <th>Notes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paiements.map(paiement => (
                        <tr key={paiement.id}>
                          <td>{new Date(paiement.date_paiement).toLocaleDateString('fr-FR')}</td>
                          <td className="montant-cell">{parseFloat(paiement.montant).toFixed(2)} €</td>
                          <td>{getModeLabel(paiement.mode_paiement)}</td>
                          <td>{paiement.reference || '-'}</td>
                          <td className="notes-cell">{paiement.notes || '-'}</td>
                          <td className="actions-cell">
                            <button className="btn-edit-small" onClick={() => openEditPaiement(paiement)} title="Modifier">
                              <Pencil size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                            </button>
                            <button className="btn-delete-small" onClick={() => deletePaiement(paiement.id)} title="Supprimer">
                              <Trash2 size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-paiements">
                    <p>Aucun paiement enregistré pour cette facture.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setViewingFacture(null)}>
                Fermer
              </button>
              <button
                className="btn-primary"
                onClick={() => downloadPDF(viewingFacture.id, viewingFacture.numero_facture)}
              >
                Télécharger PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajout/Modification Paiement */}
      {showPaiementModal && viewingFacture && (
        <div className="modal-overlay">
          <div className="modal-content modal-paiement">
            <div className="modal-header">
              <h2>{editingPaiement ? 'Modifier le paiement' : 'Enregistrer un paiement'}</h2>
              <button className="modal-close" onClick={() => setShowPaiementModal(false)}>&times;</button>
            </div>
            <form onSubmit={handlePaiementSubmit}>
              <div className="modal-body">
                <div className="paiement-facture-info">
                  <p>Facture: <strong>{viewingFacture.numero_facture}</strong></p>
                  <p>Client: {viewingFacture.client_nom}</p>
                  <p>Reste à payer: <strong>{Math.max(0, parseFloat(viewingFacture.montant_ttc) - paiements.reduce((sum, p) => sum + parseFloat(p.montant), 0)).toFixed(2)} €</strong></p>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Montant *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={paiementForm.montant}
                      onChange={e => setPaiementForm({ ...paiementForm, montant: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Date de paiement *</label>
                    <input
                      type="date"
                      value={paiementForm.date_paiement}
                      onChange={e => setPaiementForm({ ...paiementForm, date_paiement: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Mode de paiement</label>
                    <select
                      value={paiementForm.mode_paiement}
                      onChange={e => setPaiementForm({ ...paiementForm, mode_paiement: e.target.value })}
                    >
                      <option value="virement">Virement bancaire</option>
                      <option value="cheque">Chèque</option>
                      <option value="carte">Carte bancaire</option>
                      <option value="especes">Espèces</option>
                      <option value="prelevement">Prélèvement</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Référence (n° chèque, virement...)</label>
                    <input
                      type="text"
                      value={paiementForm.reference}
                      onChange={e => setPaiementForm({ ...paiementForm, reference: e.target.value })}
                      placeholder="Ex: CHQ-12345"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={paiementForm.notes}
                    onChange={e => setPaiementForm({ ...paiementForm, notes: e.target.value })}
                    rows="2"
                    placeholder="Notes sur ce paiement..."
                  />
                </div>

                {paiementForm.echeance_id && (
                  <div className="paiement-echeance-link">
                    <span>Lié à l'échéance client n°{echeancesDisponibles.find(e => e.id.toString() === paiementForm.echeance_id)?.echeance_numero || paiementForm.echeance_id}</span>
                    <button type="button" className="btn-unlink" onClick={() => setPaiementForm({ ...paiementForm, echeance_id: '' })}>
                      Délier
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowPaiementModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingPaiement ? 'Modifier' : 'Enregistrer le paiement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Création depuis Devis */}
      {showFromDevisModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header modal-header-devis">
              <h2>Créer une facture depuis un devis</h2>
              <button className="modal-close" onClick={() => { setShowFromDevisModal(false); setSelectedDevisForPaiement(null); }}>&times;</button>
            </div>
            <div className="modal-body">
              {!selectedDevisForPaiement ? (
                devisAcceptes.length === 0 ? (
                  <div className="empty-state">
                    <p>Aucun devis disponible</p>
                    <p className="text-muted">Créez d'abord un devis pour pouvoir le convertir en facture.</p>
                  </div>
                ) : (
                  <div className="devis-list-select">
                    <p className="info-text">Sélectionnez un devis pour créer une facture :</p>
                    {devisAcceptes.map(devis => (
                      <div key={devis.id} className="devis-item" onClick={() => openPaiementForDevis(devis)}>
                        <div className="devis-item-info">
                          <span className="devis-numero">{devis.numero_devis}</span>
                          <span className="devis-client">{devis.client_nom} - {devis.client_entreprise}</span>
                          <span className={`devis-statut-badge statut-${devis.statut}`}>
                            {devis.statut === 'brouillon' ? 'Brouillon' :
                             devis.statut === 'envoye' ? 'Envoyé' :
                             devis.statut === 'accepte' ? 'Accepté' : devis.statut}
                          </span>
                        </div>
                        <div className="devis-item-montant">
                          {parseFloat(devis.total_ttc).toLocaleString('fr-FR')} € TTC
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div>
                  <div style={{
                    padding: '12px',
                    background: '#f1f5f9',
                    borderRadius: '6px',
                    marginBottom: '16px'
                  }}>
                    <strong>Devis :</strong> {selectedDevisForPaiement.numero_devis} —{' '}
                    {selectedDevisForPaiement.client_nom} ({selectedDevisForPaiement.client_entreprise})
                    <br/>
                    <strong>Total TTC :</strong> {parseFloat(selectedDevisForPaiement.total_ttc).toLocaleString('fr-FR')} €
                  </div>

                  <div style={{
                    padding: '16px',
                    background: '#f0f9ff',
                    border: '2px solid #0ea5e9',
                    borderRadius: '8px'
                  }}>
                    <h3 style={{ marginTop: 0, color: '#0c4a6e' }}>
                      Paiement (obligatoire — la facture sera émise à cette date)
                    </h3>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <div className="form-group" style={{ flex: '1 1 180px' }}>
                        <label>Date du paiement *</label>
                        <input
                          type="date"
                          value={devisPaiementForm.date_paiement}
                          onChange={(e) => setDevisPaiementForm({ ...devisPaiementForm, date_paiement: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group" style={{ flex: '1 1 180px' }}>
                        <label>Montant payé (TTC) *</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={devisPaiementForm.montant_paye}
                          onChange={(e) => setDevisPaiementForm({ ...devisPaiementForm, montant_paye: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group" style={{ flex: '1 1 180px' }}>
                        <label>Mode de paiement *</label>
                        <select
                          value={devisPaiementForm.mode_paiement}
                          onChange={(e) => setDevisPaiementForm({ ...devisPaiementForm, mode_paiement: e.target.value })}
                          required
                        >
                          <option value="virement">Virement</option>
                          <option value="cb">Carte bancaire</option>
                          <option value="especes">Espèces</option>
                          <option value="cheque">Chèque</option>
                          <option value="prelevement">Prélèvement</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ flex: '1 1 180px' }}>
                        <label>Référence (facultatif)</label>
                        <input
                          type="text"
                          value={devisPaiementForm.reference_paiement}
                          onChange={(e) => setDevisPaiementForm({ ...devisPaiementForm, reference_paiement: e.target.value })}
                          placeholder="N° transaction, chèque..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {selectedDevisForPaiement && (
                <button type="button" className="btn-cancel" onClick={() => setSelectedDevisForPaiement(null)}>
                  ← Retour
                </button>
              )}
              <button className="btn-cancel" onClick={() => { setShowFromDevisModal(false); setSelectedDevisForPaiement(null); }}>
                Annuler
              </button>
              {selectedDevisForPaiement && (
                <button type="button" className="btn-primary" onClick={createFromDevis}>
                  Créer la facture
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacturesManagement;
